import {
  IconBuildingsFilled,
  IconDollarSignCircleFilled,
  IconHammerFilled,
  IconPlusFilled,
  IconSparkleFilled,
} from '@zillow/constellation-icons';
import { useMainLayout } from '../../../layouts/MainPageLayout/MainLayoutContext';
import { type Filters, regionsWithMarkers } from './MarketDataMap';
import {
  ADVERTISED_PROPERTY_MARKET_MAP_DOT_COLOR,
  CLAIMED_PROPERTY_MARKET_MAP_DOT_COLOR,
  DARK_ICON_COLOR,
} from '../../../SharedComponents/Map/Dots/Dots.consts';
import { MapControls as SharedMapControls } from '../../../SharedComponents/Map/MapControls';
import type {
  CompetitorWithOfferDto,
  PortfolioAnalyticsPropertyResponseDto,
  Property,
  TopConstructionDto,
} from '@zg-rentals/ts-customer-analytics-client';

export const MarketDataMapControls = ({
  filters,
  onFilterChange,
  isFetchingSpecialOffers,
  isFetchingNewInventory,
  isFetchingTopNewConstructions,
  advertisedProperties,
  claimedProperties,
  competitorsWithSpecialOffer,
  newInventory,
  nowLeasing,
  underConstruction,
  plannedDevelopment,
}: {
  filters: Filters;
  onFilterChange: (filter: keyof Filters, value: boolean) => void;
  isFetchingSpecialOffers: boolean;
  isFetchingNewInventory: boolean;
  isFetchingTopNewConstructions: boolean;
  advertisedProperties: Array<PortfolioAnalyticsPropertyResponseDto>;
  claimedProperties: Array<PortfolioAnalyticsPropertyResponseDto>;
  competitorsWithSpecialOffer: Array<CompetitorWithOfferDto>;
  newInventory: Array<Property>;
  nowLeasing: Array<TopConstructionDto>;
  underConstruction: Array<TopConstructionDto>;
  plannedDevelopment: Array<TopConstructionDto>;
}) => {
  const { selectedRegionId, selectedDisplayingRegionLevel, displayMode } = useMainLayout();

  const finalDisplayMode = selectedRegionId ? selectedDisplayingRegionLevel : displayMode;

  if (!regionsWithMarkers.includes(finalDisplayMode)) return null;

  const sections = [
    {
      title: "Show your portfolio's",
      controls: [
        {
          backgroundColor: ADVERTISED_PROPERTY_MARKET_MAP_DOT_COLOR,
          icon: <IconBuildingsFilled />,
          title: 'Advertised properties',
          checked: filters.showAdvertisedProperties,
          onChange: () => onFilterChange('showAdvertisedProperties', !filters.showAdvertisedProperties),
          items: advertisedProperties,
        },
        {
          backgroundColor: CLAIMED_PROPERTY_MARKET_MAP_DOT_COLOR,
          iconColor: DARK_ICON_COLOR,
          icon: <IconBuildingsFilled />,
          title: 'Claimed properties',
          checked: filters.showClaimedProperties,
          onChange: () => onFilterChange('showClaimedProperties', !filters.showClaimedProperties),
          items: claimedProperties,
        },
      ],
    },
    {
      title: 'Show competitors',
      isLoading: isFetchingSpecialOffers || isFetchingNewInventory,
      popupText:
        'A competitor is identified as a property that’s getting views from the same prospective renters as yours.',
      controls: [
        {
          icon: <IconDollarSignCircleFilled />,
          title: 'Special offers',
          checked: filters.showSpecialOffers,
          onChange: () => onFilterChange('showSpecialOffers', !filters.showSpecialOffers),
          items: competitorsWithSpecialOffer,
        },
        {
          icon: <IconPlusFilled />,
          title: 'New inventory on Zillow',
          popupText: 'The 20 most recent properties that added new units to Zillow in the last 60 days.',
          checked: filters.showNewInventoryOnZillow,
          onChange: () => onFilterChange('showNewInventoryOnZillow', !filters.showNewInventoryOnZillow),
          items: newInventory,
        },
      ],
    },
    {
      title: 'Show new construction',
      isLoading: isFetchingTopNewConstructions,
      controls: [
        {
          icon: <IconSparkleFilled />,
          title: 'Now leasing',
          checked: filters.showNowLeasing,
          onChange: () => onFilterChange('showNowLeasing', !filters.showNowLeasing),
          items: nowLeasing,
        },
        {
          icon: <IconHammerFilled />,
          title: 'Under construction',
          checked: filters.showUnderConstruction,
          onChange: () => onFilterChange('showUnderConstruction', !filters.showUnderConstruction),
          items: underConstruction,
        },
        {
          icon: <IconHammerFilled />,
          title: 'Planned development',
          checked: filters.showPlannedDevelopment,
          onChange: () => onFilterChange('showPlannedDevelopment', !filters.showPlannedDevelopment),
          items: plannedDevelopment,
        },
      ],
    },
  ];

  return <SharedMapControls sections={sections} />;
};
